"use client";
import React, { useEffect, useRef, useState } from "react";
import sponser1 from "@/public/sponser1.png";
import sponser2 from "@/public/sponser2.png";
import sponser3 from "@/public/sponser3.png";
import sponser5 from "@/public/sponser5.png";
import sponser6 from "@/public/sponser6.png";
import sponser7 from "@/public/sponser7.png";

import Image from "next/image";
import { usePathname } from "next/navigation";
import userService from "@/services/user-service";
import { findCityFromSlug } from "@/utils/citySlug";

const SponsorComponent = ({ text = "", region, city }) => {
  const pathname = usePathname();
  const [sponsors, setSponsors] = useState([]);
  const [needsScroll, setNeedsScroll] = useState(true);
  const containerRef = useRef(null);
  const measureRowRef = useRef(null);

  let sponsorPage = "";
  if (pathname.includes("osmrtnice")) {
    sponsorPage = "osmrtnice";
  } else if (pathname.includes("pogrebi")) {
    sponsorPage = "pogrebi";
  } else if (pathname.includes("cvetlicarne")) {
    sponsorPage = "cvetlicarne";
  } else if (pathname.includes("pogrebna-p")) {
    sponsorPage = "pogrebna podjetja";
  }

  useEffect(() => {
    const fetchList = async () => {
      // Decode the city prop to handle cases where it might be already encoded
      const decodedCity = decodeURIComponent(city || "");

      // Normalize city name to handle slugs with + or - correctly
      let normalizedCity = findCityFromSlug(decodedCity) || decodedCity;

      // Check for "show all" variants
      const isShowAll =
        !normalizedCity ||
        normalizedCity === "- Pokaži vse občine -" ||
        normalizedCity === "allCities" ||
        (typeof normalizedCity === "string" &&
          normalizedCity.includes("pokazi-vse"));

      if (isShowAll) {
        normalizedCity = "";
      }

      // Construct query string manually to ensure literal + for spaces as requested
      // and avoid double encoding by axios params
      const encodedCity = encodeURIComponent(normalizedCity || "").replace(
        /%20/g,
        "+",
      );
      const encodedRegion = encodeURIComponent(region || "");
      const encodedPage = encodeURIComponent(sponsorPage || "");

      const queryString = `?region=${encodedRegion}&city=${encodedCity}&page=${encodedPage}`;

      const res = await userService.getSponsors(queryString);
      setSponsors(Array.isArray(res?.data) ? res.data : []);
    };
    if (region || city || pathname) {
      fetchList();
    }
  }, [region, city, pathname]);

  const fallbackLogos = [
    { src: sponser6.src, alt: "Partner – No Sponsors" },
    { src: sponser7.src, alt: "Partner – No Sponsors" },
    { src: sponser6.src, alt: "Partner – No Sponsors" },
    { src: sponser7.src, alt: "Partner – No Sponsors" },
  ];

  const normalizeLink = (link) => {
    if (!link || typeof link !== "string") return null;
    const trimmed = link.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const items = sponsors?.length > 0
    ? sponsors.map((item) => {
      const logoUrl =
        item?.logo && typeof item.logo === "string"
          ? item.logo
          : sponser6.src || sponser6;
      return {
        id: item?.id,
        logoUrl,
        alt: `Partner – ${item?.name || ""}${item?.city ? `, ${item.city}` : ""}`,
        websiteLink: normalizeLink(item?.websiteLink),
        hasLink: true,
      };
    })
    : fallbackLogos.map((src, i) => ({
      id: `fallback-${i}`,
      logoUrl: src.src,
      alt: src.alt,
      websiteLink: null,
      hasLink: false,
    }));

  const logoClasses =
    "h-[45px] tablet:h-[60px] desktop:h-[80px] w-auto max-w-[200px] object-contain object-center flex-shrink-0 filter grayscale " +
    (sponsors?.length > 0 ? "opacity-80 hover:opacity-100 transition-opacity" : "");

  const renderLogo = (item) => (
    <img
      src={item.logoUrl}
      alt={item.alt}
      className={logoClasses}
    />
  );

  const carouselCopies = items.length <= 4 ? 24 : 2;
  const translatePercent = 100 / carouselCopies;

  useEffect(() => {
    const container = containerRef.current;
    const row = measureRowRef.current;
    if (!container || !row || items.length === 0) return;

    const update = () => {
      const cw = container.getBoundingClientRect().width;
      const rw = row.getBoundingClientRect().width;
      setNeedsScroll(rw > cw);
    };

    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(container);
    resizeObserver.observe(row);
    return () => resizeObserver.disconnect();
  }, [items]);

  if ((pathname === "/pogrebi" || pathname === "/pogrebi/") && !region && !city) {
    return null;
  }

  if ((pathname === "/osmrtnice" || pathname === "/osmrtnice/") && !region && !city) {
    return null;
  }


  const carouselRow = (copyIndex) => (
    <React.Fragment key={copyIndex}>
      {items.map((item) => (
        <div
          key={`${item.id}-${copyIndex}`}
          className="flex h-[45px] tablet:h-[60px] desktop:h-[80px] items-center justify-center flex-shrink-0 px-5"
        >
          {item.websiteLink ? (
            <a
              href={item.websiteLink}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer"
            >
              {renderLogo(item)}
            </a>
          ) : (
            renderLogo(item)
          )}
        </div>
      ))}
    </React.Fragment>
  );

  return (
    <div className="bg-[#F8F9FA] border-t border-[#E5E7EB]">
      <style>{`
        @keyframes sponsorCarousel {
          0% { transform: translateX(0) translateZ(0); }
          100% { transform: translateX(-${translatePercent}%) translateZ(0); }
        }
      `}</style>
      <div
        className="
      desktop:py-24
      tablet:py-[70px]
      mobile:py-[40px]
      relative max-w-[1920px] overflow-hidden mx-auto flex justify-center items-center"
      >
        <div className="flex flex-col items-center justify-center w-full px-4 max-w-[1800px]">
          <div
            className={`text-[#6B7280] font-variation-customOpt24 mobile:text-[20px] text-[28px] text-center mb-4`}
          >
            {sponsors?.length > 0
              ? (text ? `${text}` : "Brez njihove pomoči ne bi šlo")
              : "To stran so omogočili"}
          </div>
          <div
            ref={containerRef}
            className="w-full overflow-hidden mt-6 relative"
          >
            <div
              ref={measureRowRef}
              className="flex items-center justify-start flex-shrink-0"
              style={{
                position: "absolute",
                left: "-9999px",
                visibility: "hidden",
                width: "max-content",
              }}
              aria-hidden="true"
            >
              {carouselRow(0)}
            </div>
            {needsScroll ? (
              <div
                className="flex items-center justify-start will-change-transform"
                style={{
                  width: "max-content",
                  animation: "sponsorCarousel 60s linear infinite",
                }}
              >
                {Array.from(
                  { length: carouselCopies },
                  (_, i) => carouselRow(i)
                )}
              </div>
            ) : (
              <div className="flex justify-center items-center">
                <div
                  className="flex items-center justify-start"
                  style={{ width: "max-content" }}
                >
                  {carouselRow(0)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorComponent;
